import type { FunctionComponent } from "react"
import { useState, useEffect, useCallback, useRef } from "react"

import HeaderColorBarV2 from "~/components/header/HeaderColorBar"
import HeaderMobileMenuV2 from "~/components/header/HeaderMobileMenu"
import type {
  WithContentfulHeaderLocationFragment,
  WithExperienceFragment,
  WithLinkFragment,
  WithStoriesFragment
} from "~/contentful"
import { useLocation } from "react-router-dom"
import StoriesGroup from "./StoriesGroup"
import type { SerializeFrom } from "@remix-run/node"
import { ClientOnly } from "remix-utils/client-only"
import {
  contentfulImageUrl,
  getFeaturedExperiences,
  getOtherExperiences
} from "~/helpers/contentful"
import { MOBILE_SRCSET_WIDTHS } from "~/helpers/responsiveImage"

export type MobileMenuState = "closed" | "open" | "locations"

export interface Props {
  showSearch?: boolean
  bookingMode?: boolean
  customBackgroundColor?: string
  locationPages: SerializeFrom<WithContentfulHeaderLocationFragment>[]
  experiences: SerializeFrom<WithExperienceFragment>[]
  eventLinks: SerializeFrom<WithLinkFragment>[]
  activityLinks: SerializeFrom<WithLinkFragment>[]
  stories: SerializeFrom<WithStoriesFragment[]>
  loggedIn?: boolean
  locationSlug?: string | null
  shopPromoLinks?: SerializeFrom<WithLinkFragment>[]
}

const skipNavigationId = "skip-navigation"

const HeaderV2: FunctionComponent<Props> = ({
  showSearch = true,
  bookingMode = false,
  customBackgroundColor,
  locationPages,
  experiences,
  eventLinks,
  activityLinks,
  stories,
  loggedIn,
  locationSlug,
  shopPromoLinks
}) => {
  const [mobileMenuState, setMobileMenuState] =
    useState<MobileMenuState>("closed")
  const preloadedRef = useRef(false)

  // Close mobile menu on route change.
  const location = useLocation()
  useEffect(() => {
    setMobileMenuState("closed")
  }, [location])

  // Preload mobile menu images when triggered (e.g., onClick)
  const preloadMobileMenuImages = useCallback(() => {
    if (preloadedRef.current) return
    preloadedRef.current = true

    const featuredExperiences = getFeaturedExperiences(experiences)
    const otherExperiences = getOtherExperiences(
      experiences,
      featuredExperiences
    )

    const imageUrls = [
      ...featuredExperiences.map(e => e.image?.url),
      ...otherExperiences.map(e => e.image?.url),
      ...eventLinks.slice(0, 6).map(l => l.image?.url),
      ...activityLinks.slice(0, 6).map(l => l.image?.url)
    ].filter(Boolean) as string[]

    imageUrls.forEach(imageUrl => {
      // Build srcset for AVIF
      const avifSrcset = MOBILE_SRCSET_WIDTHS.map(
        width =>
          `${contentfulImageUrl(imageUrl, {
            width,
            format: "avif",
            quality: 75,
            fit: "fill",
            focus: "center"
          })} ${width}w`
      ).join(", ")

      // Build srcset for WebP
      const webpSrcset = MOBILE_SRCSET_WIDTHS.map(
        width =>
          `${contentfulImageUrl(imageUrl, {
            width,
            format: "webp",
            quality: 75,
            fit: "fill",
            focus: "center"
          })} ${width}w`
      ).join(", ")

      // Preload AVIF variant
      const avifLink = document.createElement("link")
      Object.assign(avifLink, {
        rel: "preload",
        as: "image",
        imagesrcset: avifSrcset,
        imagesizes: "200px",
        type: "image/avif"
      })
      document.head.appendChild(avifLink)

      // Preload WebP variant (fallback for browsers without AVIF support)
      const webpLink = document.createElement("link")
      Object.assign(webpLink, {
        rel: "preload",
        as: "image",
        imagesrcset: webpSrcset,
        imagesizes: "200px",
        type: "image/webp"
      })
      document.head.appendChild(webpLink)
    })
  }, [experiences, eventLinks, activityLinks])

  return (
    <>
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 xl:focus:left-16 2xl:focus:left-56 focus:bg-[#FF0000] focus:p-2 focus:text-white focus:z-max"
        href={`#${skipNavigationId}`}
      >
        Skip to content
      </a>

      <header className="relative z-50">
        <HeaderColorBarV2
          customBackgroundColor={customBackgroundColor}
          showSearch={showSearch}
          bookingMode={bookingMode}
          setMobileMenuState={setMobileMenuState}
          preloadMobileMenuImages={preloadMobileMenuImages}
          locationPages={locationPages}
          activityLinks={activityLinks}
          loggedIn={loggedIn}
          locationSlug={locationSlug}
        />
      </header>
      {!bookingMode && (
        <HeaderMobileMenuV2
          mobileMenuState={mobileMenuState}
          locationPages={locationPages}
          experiences={experiences}
          eventLinks={eventLinks}
          activityLinks={activityLinks}
          onClose={() => setMobileMenuState("closed")}
          showSearch={showSearch}
          customBackgroundColor={customBackgroundColor}
          locationSlug={locationSlug}
          shopPromoLinks={shopPromoLinks}
        />
      )}

      <ClientOnly>{() => <StoriesGroup stories={stories} />}</ClientOnly>

      <div id={skipNavigationId} />
    </>
  )
}
export default HeaderV2
